// dna-relevance.ts — pure helper mapping market name → which DNA
// profiles inject a meaningful signal for that market.
//
// Source of truth: `Every Document/Agents Reasoning:Logic/
// STRIKZA_DNA_Full_Specification.docx` — every profile's
// "MARKETS AFFECTED" section. Phase F ships the contract; Phase F5+
// per-profile builders write the actual rows.
//
// Doctrine: this is a recommendation overlay, not a fabrication. The
// badge only renders for active profiles — `relevantProfilesForMarket`
// merely names the universe of profiles a market COULD draw on. The
// read API still gates on sample_valid before any UI rendering.

export type DNAProfileName =
  | "manager_dna"
  | "player_dna"
  | "referee_dna"
  | "venue_dna"
  | "storyline_dna"
  | "goalkeeper_dna"
  | "injury_cascade_dna"
  | "travel_congestion_dna"
  | "h2h_psychological_dna"
  | "weather_response_dna"
  | "set_piece_dna"
  | "big_game_dna"
  | "referee_manager_conflict_dna";

export const ALL_DNA_PROFILES: readonly DNAProfileName[] = [
  "manager_dna",
  "player_dna",
  "referee_dna",
  "venue_dna",
  "storyline_dna",
  "goalkeeper_dna",
  "injury_cascade_dna",
  "travel_congestion_dna",
  "h2h_psychological_dna",
  "weather_response_dna",
  "set_piece_dna",
  "big_game_dna",
  "referee_manager_conflict_dna",
];

/** Lowercase a market label and strip non-alphanumerics for matching. */
function normalize(market: string): string {
  return market.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

/**
 * Map a free-text market name to the DNA profiles whose "MARKETS
 * AFFECTED" section names that family of markets. The mapping is a
 * union of every profile's stated relevance — it does NOT consider
 * which profile is active for a given fixture; that's the read API's
 * job.
 *
 * Returns a stable, alphabetically-sorted readonly array. Empty array
 * when no profile claims relevance (the badge then renders nothing).
 */
export function relevantProfilesForMarket(
  market: string,
): readonly DNAProfileName[] {
  const m = normalize(market);
  const set = new Set<DNAProfileName>();

  // Result / 1X2 / Match Result / Win-Draw-Win
  if (
    m.includes("result") ||
    m === "1x2" ||
    m === "match_winner" ||
    m === "match_result" ||
    m === "wdw"
  ) {
    set.add("manager_dna");
    set.add("storyline_dna");
    set.add("h2h_psychological_dna");
    set.add("big_game_dna");
    set.add("referee_dna");
    set.add("injury_cascade_dna");
    set.add("venue_dna");
    set.add("weather_response_dna");
  }

  // Total goals (Over/Under, anytime)
  if (
    m.includes("over") ||
    m.includes("under") ||
    m.includes("total") ||
    m.includes("goals")
  ) {
    set.add("set_piece_dna");
    set.add("travel_congestion_dna");
    set.add("weather_response_dna");
    set.add("storyline_dna");
    set.add("big_game_dna");
    set.add("h2h_psychological_dna");
    set.add("injury_cascade_dna");
    set.add("venue_dna");
  }

  // BTTS
  if (m.includes("btts") || m.includes("both_teams") || m === "gg" || m === "ng") {
    set.add("goalkeeper_dna");
    set.add("set_piece_dna");
    set.add("weather_response_dna");
    set.add("h2h_psychological_dna");
  }

  // Clean sheet / win to nil
  if (m.includes("clean_sheet") || m.includes("win_to_nil")) {
    set.add("goalkeeper_dna");
    set.add("storyline_dna");
    set.add("injury_cascade_dna");
    set.add("set_piece_dna");
  }

  // Goalscorer markets (anytime, first, last)
  if (
    m.includes("goalscorer") ||
    m.includes("anytime_scorer") ||
    m.includes("first_goal") ||
    m.includes("last_goal") ||
    m === "ags"
  ) {
    set.add("player_dna");
    set.add("goalkeeper_dna");
    set.add("injury_cascade_dna");
    set.add("set_piece_dna");
  }

  // Cards markets
  if (m.includes("card") || m.includes("yellow") || m.includes("red_card") || m.includes("booking")) {
    set.add("referee_dna");
    set.add("manager_dna");
    set.add("h2h_psychological_dna");
    set.add("big_game_dna");
    set.add("referee_manager_conflict_dna");
    set.add("storyline_dna");
  }

  // Corners markets
  if (m.includes("corner")) {
    set.add("set_piece_dna");
    set.add("manager_dna");
  }

  // Asian handicap / margin
  if (m.includes("handicap") || m.includes("margin") || m.includes("ah_") || m === "ah") {
    set.add("travel_congestion_dna");
    set.add("manager_dna");
    set.add("big_game_dna");
  }

  // Correct Score — broad signal set, treated like result + goals.
  if (m.includes("correct_score") || m === "cs") {
    set.add("manager_dna");
    set.add("storyline_dna");
    set.add("h2h_psychological_dna");
    set.add("set_piece_dna");
    set.add("goalkeeper_dna");
    set.add("weather_response_dna");
  }

  return Object.freeze(
    Array.from(set).sort() as DNAProfileName[],
  ) as readonly DNAProfileName[];
}

/** Human-readable short label per profile, used in the badge tooltip. */
export const DNA_PROFILE_LABEL: Readonly<Record<DNAProfileName, string>> = Object.freeze({
  manager_dna: "Manager",
  player_dna: "Player",
  referee_dna: "Referee",
  venue_dna: "Venue",
  storyline_dna: "Storyline",
  goalkeeper_dna: "Goalkeeper",
  injury_cascade_dna: "Injury Cascade",
  travel_congestion_dna: "Travel & Congestion",
  h2h_psychological_dna: "H2H Psychological",
  weather_response_dna: "Weather Response",
  set_piece_dna: "Set Piece",
  big_game_dna: "Big Game",
  referee_manager_conflict_dna: "Ref-Manager Conflict",
});
